import React, { useRef } from "react";
import { cn } from "@/lib/utils";
import { LosLogo } from "@/components/Logo";
import { money, lineTotal, longDate } from "@/lib/sk";

const L = {
  en: { proposal: "Proposal", prepared: "Prepared for", vessel: "Vessel", date: "Date", valid: "Valid until", version: "Version", summary: "Executive summary", understanding: "Our understanding", scope: "Scope of supply", technical: "Technical description", delivery: "Delivery and schedule", price: "Price summary", assumptions: "Assumptions", exclusions: "Exclusions", terms: "Commercial terms", total: "Total, excl. VAT", qty: "Qty", contact: "Your contact", excl: "All prices excluding VAT" },
  no: { proposal: "Tilbud", prepared: "Utarbeidet for", vessel: "Fartøy", date: "Dato", valid: "Gyldig til", version: "Versjon", summary: "Sammendrag", understanding: "Vår forståelse", scope: "Leveranseomfang", technical: "Teknisk beskrivelse", delivery: "Leveranse og fremdrift", price: "Prissammendrag", assumptions: "Forutsetninger", exclusions: "Ikke inkludert", terms: "Kommersielle vilkår", total: "Totalt eks. mva.", qty: "Antall", contact: "Din kontakt", excl: "Alle priser eks. mva." },
};

function Editable({ value, onCommit, as: Tag = "p", className, multiline = true, testId, editable }) {
  const ref = useRef(null);
  if (!editable) return <Tag className={cn("whitespace-pre-wrap", className)} data-testid={testId}>{value}</Tag>;
  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => {
        const t = e.currentTarget.innerText.trim();
        if (t !== (value || "").trim()) onCommit(t);
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className={cn("editable whitespace-pre-wrap", className)}
      data-testid={testId}
    >
      {value}
    </Tag>
  );
}

function EditableList({ items, onCommit, editable, testId }) {
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {(items || []).map((x, i) => (
        <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed text-foreground/85">
          <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-primary" />
          <Editable as="span" value={x} editable={editable} multiline={false} className="min-w-0 flex-1" onCommit={(t) => { const next = [...items]; if (t) next[i] = t; else next.splice(i, 1); onCommit(next); }} />
        </li>
      ))}
    </ul>
  );
}

/**
 * The customer-ready quote. Used inside the canvas (editable) and on the public /q/:token page (read-only).
 * Never shows cost or margin.
 */
export function QuoteDocument({ quote, company, customer, owner, editable = false, onEdit, className, testId = "quote-document" }) {
  const lang = quote.language === "no" ? "no" : "en";
  const T = L[lang];
  const s = quote.sections || {};
  const items = quote.items || quote.items_snapshot || [];
  const currency = quote.currency || "NOK";
  const rate = quote.fx_rate || 1;
  const M = (v) => money(v, currency, rate);
  const groups = [];
  items.forEach((it) => {
    const g = groups.find((x) => x.name === (it.group || it.category));
    const lt = it.line_total ?? lineTotal(it);
    if (g) g.items.push(it), (g.total += lt);
    else groups.push({ name: it.group || it.category, items: [it], total: lt });
  });
  const total = groups.reduce((a, g) => a + g.total, 0);
  const edit = (key) => (v) => onEdit && onEdit(key, v);
  const locale = lang === "no" ? "nb-NO" : "en-GB";
  const vessel = quote.vessel || {};

  return (
    <article className={cn("mx-auto w-full max-w-[860px] bg-white text-foreground", className)} data-testid={testId}>
      <header className="flex items-start justify-between gap-6 border-b border-black/[0.08] pb-8">
        <div>
          <LosLogo className="text-[20px]" />
          <div className="mt-1.5 text-[11.5px] text-muted-foreground">{company?.legal_name || "LOS Marine AS"} · {company?.address || "Rubbestadneset, Norway"}</div>
        </div>
        <div className="text-right text-[12px] leading-relaxed text-muted-foreground">
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary">{T.proposal} · {T.version} {quote.version}</div>
          <div>{T.date}: {longDate(quote.sent_at || quote.created_at, locale)}</div>
          <div>{T.valid}: {longDate(quote.valid_until, locale)}</div>
        </div>
      </header>

      <div className="pt-10">
        <div className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.prepared}</div>
        <div className="mt-1 text-[15px] font-medium">{customer?.name || quote.customer_name}{vessel?.name ? <span className="text-muted-foreground"> · {T.vessel} {vessel.name}{vessel.type ? `, ${vessel.type}` : ""}</span> : null}</div>
        <Editable as="h1" value={s.title || quote.title} editable={editable} multiline={false} onCommit={edit("title")} className="mt-5 font-display text-[30px] font-bold leading-[1.1] tracking-[-0.03em] sm:text-[36px]" testId="quote-title" />
      </div>

      <Section title={T.summary}><Editable value={s.executive_summary} editable={editable} onCommit={edit("executive_summary")} className="text-[14.5px] leading-[1.75] text-foreground/90" testId="quote-executive-summary" /></Section>
      {s.our_understanding ? <Section title={T.understanding}><Editable value={s.our_understanding} editable={editable} onCommit={edit("our_understanding")} className="text-[14px] leading-[1.75] text-foreground/85" /></Section> : null}

      <Section title={T.scope}>
        <div className="grid gap-x-10 gap-y-5 sm:grid-cols-2" data-testid="quote-scope">
          {(quote.scope || groups.map((g) => ({ group: g.name, items: g.items.map((it) => ({ name: it.name, qty: it.qty, unit: it.unit })) }))).map((g) => (
            <div key={g.group}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">{g.group}</div>
              <ul className="mt-1.5 space-y-1">
                {g.items.map((it, i) => <li key={i} className="flex justify-between gap-3 text-[13px] text-foreground/85"><span>{it.name}</span><span className="shrink-0 font-mono text-[12px] tabular-nums text-muted-foreground">{it.qty} {it.unit}</span></li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section title={T.technical}><Editable value={s.technical_description} editable={editable} onCommit={edit("technical_description")} className="text-[14px] leading-[1.75] text-foreground/85" testId="quote-technical" /></Section>
      {s.delivery_and_schedule ? <Section title={T.delivery}><Editable value={s.delivery_and_schedule} editable={editable} onCommit={edit("delivery_and_schedule")} className="text-[14px] leading-[1.75] text-foreground/85" /></Section> : null}

      <Section title={T.price}>
        <table className="w-full border-collapse" data-testid="quote-price-table">
          <tbody>
            {groups.map((g) => (
              <tr key={g.name} className="border-t border-black/[0.06]">
                <td className="py-2.5 text-[13.5px]">{g.name}</td>
                <td className="py-2.5 text-right font-mono text-[13px] tabular-nums">{M(g.total)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-foreground">
              <td className="pt-3 text-[14px] font-semibold">{T.total}</td>
              <td className="pt-3 text-right font-mono text-[18px] font-semibold tabular-nums" data-testid="quote-total">{M(total)}</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-2 text-[11.5px] text-muted-foreground">{T.excl}{currency !== "NOK" ? ` · ${lang === "no" ? "omregnet fra NOK, kurs" : "converted from NOK at"} ${rate}` : ""}</p>
      </Section>

      <div className="grid gap-10 pt-10 sm:grid-cols-2">
        <div><h3 className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.assumptions}</h3><div className="mt-3"><EditableList items={s.assumptions} editable={editable} onCommit={edit("assumptions")} testId="quote-assumptions" /></div></div>
        <div><h3 className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{T.exclusions}</h3><div className="mt-3"><EditableList items={s.exclusions} editable={editable} onCommit={edit("exclusions")} testId="quote-exclusions" /></div></div>
      </div>
      <Section title={T.terms}><EditableList items={s.terms} editable={editable} onCommit={edit("terms")} testId="quote-terms" /></Section>

      <footer className="mt-12 flex flex-wrap items-end justify-between gap-6 border-t border-black/[0.08] pt-8">
        <div className="max-w-[460px]"><Editable value={s.closing} editable={editable} onCommit={edit("closing")} className="text-[14px] leading-[1.7] text-foreground/85" /></div>
        {owner?.name ? (
          <div className="text-right text-[12.5px] leading-relaxed">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{T.contact}</div>
            <div className="mt-1 font-medium">{owner.name}</div>
            <div className="text-muted-foreground">{owner.title}</div>
            <div className="text-muted-foreground">{owner.email}</div>
          </div>
        ) : null}
      </footer>
    </article>
  );
}

function Section({ title, children }) {
  return (
    <section className="pt-10">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}
